import { useState, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchAgents } from '../slices/agentsSlice'
import { fetchAllFailuresForSelection } from '../slices/failuresSlice'
import type { Agent } from '../slices/agentsSlice'
import type { FailureTag, MinimalFailure } from '../slices/failuresSlice'
import './NewRunDialog.css'

interface NewRunDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    agent_ids: string[]
    selected_tags: string[]
    selected_failures: string[]
    attempts_count: number
    refined: boolean
  }) => Promise<void>
}

function NewRunDialog({ open, onClose, onSubmit }: NewRunDialogProps) {
  const dispatch = useAppDispatch()
  const { items: agents } = useAppSelector((state) => state.agents)
  const { tags, selectionItems: failures } = useAppSelector((state) => state.failures)

  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [selectedFailureIds, setSelectedFailureIds] = useState<string[]>([])
  const [attemptsCount, setIterationsCount] = useState(1)
  const [expandedTags, setExpandedTags] = useState<string[]>([])
  const [refined, setRefined] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [agentError, setAgentError] = useState('')
  const [selectionError, setSelectionError] = useState('')

  useEffect(() => {
    if (open) {
      dispatch(fetchAgents())
      dispatch(fetchAllFailuresForSelection())
    }
  }, [open, dispatch])

  // Organize failures by their tag_names (a failure can appear in multiple tags)
  const failuresByTag = useMemo(() => {
    const map = new Map<string, MinimalFailure[]>()
    for (const f of failures) {
      const names = f.tag_names && f.tag_names.length > 0 ? f.tag_names : [f.tag_name || 'Untagged']
      for (const tn of names) {
        if (!map.has(tn)) map.set(tn, [])
        map.get(tn)!.push(f)
      }
    }
    return map
  }, [failures])

  if (!open) return null

  const selectedAgents = agents.filter((a: Agent) => selectedAgentIds.includes(a.id))

  function toggleAgent(agentId: string) {
    setSelectedAgentIds((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    )
  }

  function toggleTag(tagName: string) {
    setSelectedTagIds((prev) =>
      prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName]
    )
  }

  function toggleFailure(failureId: string) {
    setSelectedFailureIds((prev) =>
      prev.includes(failureId) ? prev.filter((f) => f !== failureId) : [...prev, failureId]
    )
  }

  function toggleExpanded(tagName: string) {
    setExpandedTags((prev) =>
      prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName]
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let hasError = false
    if (selectedAgentIds.length === 0) {
      setAgentError('Please select at least one agent')
      hasError = true
    } else {
      setAgentError('')
    }
    if (selectedTagIds.length === 0 && selectedFailureIds.length === 0) {
      setSelectionError('Please select at least one tag or failure')
      hasError = true
    } else {
      setSelectionError('')
    }
    if (hasError) return
    setSubmitting(true)
    setSubmitError('')
    try {
      await onSubmit({
        agent_ids: selectedAgentIds,
        selected_tags: selectedTagIds,
        selected_failures: selectedFailureIds,
        attempts_count: attemptsCount,
        refined,
      })
      resetForm()
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An unexpected error occurred')
    } finally {
      setSubmitting(false)
    }
  }

  function resetForm() {
    setSelectedAgentIds([])
    setAgentDropdownOpen(false)
    setSelectedTagIds([])
    setSelectedFailureIds([])
    setIterationsCount(1)
    setRefined(false)
    setExpandedTags([])
    setAgentError('')
    setSelectionError('')
    setSubmitError('')
  }

  function handleCancel() {
    resetForm()
    onClose()
  }

  return (
    <div data-testid="new-run-dialog-overlay" className="dialog-overlay" onClick={handleCancel}>
      <div
        data-testid="new-run-dialog"
        className="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog-header">
          <h2 className="dialog-title">New Run Configuration</h2>
          <button
            type="button"
            data-testid="close-run-dialog-btn"
            className="dialog-close-btn"
            onClick={handleCancel}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          <div className="new-run-agent-section">
            <label className="new-run-agent-label">
              Select Agent(s) <span className="required">(Required)</span>
            </label>
            <div data-testid="run-agent-select" className="custom-select-wrapper">
              <button
                type="button"
                className="custom-select-trigger"
                onClick={() => setAgentDropdownOpen(!agentDropdownOpen)}
              >
                {selectedAgents.length > 0
                  ? selectedAgents.map((a) => a.name).join(', ')
                  : 'Select agents...'}
                <span className="select-chevron">{agentDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {agentDropdownOpen && (
                <div className="custom-dropdown">
                  {agents.map((agent: Agent) => (
                    <button
                      key={agent.id}
                      type="button"
                      data-testid={`run-agent-option-${agent.id}`}
                      className={`custom-dropdown-item ${selectedAgentIds.includes(agent.id) ? 'selected' : ''}`}
                      onClick={() => toggleAgent(agent.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAgentIds.includes(agent.id)}
                        onChange={() => {}}
                        style={{ marginRight: '8px', pointerEvents: 'none' }}
                      />
                      {agent.name}
                    </button>
                  ))}
                  {agents.length === 0 && (
                    <div className="custom-dropdown-item" style={{ color: 'var(--color-text-muted)' }}>
                      No agents available
                    </div>
                  )}
                </div>
              )}
            </div>
            {selectedAgents.length > 0 && (
              <div className="new-run-selected-agents" data-testid="selected-agents-list">
                {selectedAgents.map((agent) => (
                  <span key={agent.id} className="new-run-agent-badge" data-testid={`selected-agent-badge-${agent.id}`}>
                    {agent.name}
                    <button
                      type="button"
                      className="new-run-agent-badge-remove"
                      onClick={() => toggleAgent(agent.id)}
                      aria-label={`Remove ${agent.name}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            {agentError && (
              <span data-testid="agent-error" className="dialog-field-error">{agentError}</span>
            )}
          </div>

          <div className="new-run-selections-section">
            <span className="new-run-selections-title">
              Select Test Failure Tags and/or Specific Test Failures
            </span>

            <div data-testid="run-tag-checkboxes" className="new-run-tag-list">
              {tags.map((tag: FailureTag) => {
                const isExpanded = expandedTags.includes(tag.name)
                const tagFailures = failuresByTag.get(tag.name) || []
                const isTagChecked = selectedTagIds.includes(tag.name)
                const selectedCount = tagFailures.filter((f) => selectedFailureIds.includes(f.id)).length
                const hasPartialSelection = !isTagChecked && selectedCount > 0 && selectedCount < tagFailures.length
                return (
                  <div key={tag.name} className="new-run-tag-item" data-testid={`run-tag-${tag.name}`}>
                    <div className="new-run-tag-row">
                      <button
                        type="button"
                        className="new-run-expand-btn"
                        data-testid={`expand-tag-${tag.name}`}
                        onClick={() => toggleExpanded(tag.name)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? '▾' : '▸'}
                      </button>
                      <label className="new-run-tag-label">
                        <input
                          type="checkbox"
                          checked={isTagChecked}
                          ref={(el) => { if (el) el.indeterminate = hasPartialSelection }}
                          onChange={() => toggleTag(tag.name)}
                        />
                        <span className="new-run-tag-name">{tag.name}</span>
                        <span className="new-run-tag-count">({tag.count})</span>
                      </label>
                    </div>
                    {isExpanded && tagFailures.length > 0 && (
                      <div data-testid="run-failure-checkboxes" className="new-run-failure-list">
                        {tagFailures.map((failure: MinimalFailure) => (
                          <label
                            key={failure.id}
                            className="new-run-failure-label"
                            data-testid={`run-failure-${failure.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedFailureIds.includes(failure.id)}
                              onChange={() => toggleFailure(failure.id)}
                            />
                            {failure.short_id && <span className="new-run-failure-slug">{failure.short_id}</span>}
                            {failure.title}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {tags.length === 0 && failures.length === 0 && (
              <div style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>
                No tags or failures available.
              </div>
            )}

            {selectionError && (
              <span data-testid="selection-error" className="dialog-field-error">{selectionError}</span>
            )}
          </div>

          <div className="new-run-container-count-section">
            <label className="new-run-agent-label" htmlFor="attempts-count-input">
              Attempts per Failure
            </label>
            <p className="new-run-container-count-description">
              Number of times each agent attempts each failure (1–10).
            </p>
            <input
              id="attempts-count-input"
              data-testid="attempts-count-input"
              type="text"
              inputMode="numeric"
              className="new-run-container-count-input"
              value={attemptsCount}
              onChange={(e) => {
                const raw = e.target.value.replace(/\D/g, '')
                if (raw === '') { setIterationsCount('' as unknown as number); return }
                const n = Math.max(1, Math.min(10, Number(raw)))
                setIterationsCount(n)
              }}
              onBlur={() => {
                if (!attemptsCount || attemptsCount < 1) setIterationsCount(1)
              }}
            />
          </div>

          <div className="new-run-container-count-section">
            <label className="new-run-agent-label new-run-refined-label">
              <input
                data-testid="refined-toggle"
                type="checkbox"
                checked={refined}
                onChange={(e) => setRefined(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Refined Mode
            </label>
            <p className="new-run-container-count-description">
              Use refined branches and prompt agents to explain the root cause.
            </p>
          </div>

          {submitError && (
            <div data-testid="submit-error" className="dialog-field-error" style={{ marginBottom: '12px' }}>
              {submitError}
            </div>
          )}

          <div className="dialog-actions">
            <button
              type="button"
              data-testid="cancel-run-btn"
              className="dialog-btn-cancel"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="start-run-btn"
              className="dialog-btn-submit"
              disabled={submitting}
            >
              {submitting ? 'Starting...' : 'Start Run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default NewRunDialog
